import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, DollarSign, ArrowUpDown, RefreshCw, Eye, Clock, Repeat, Info, ArrowLeft, AlertCircle, PieChart, BarChart3, Menu, X, CheckCircle, } from "lucide-react";
import { Button } from "./ui/button";
import Breadcrumb from './Breadcrumb';
import InvestmentLotsSection from './InvestmentLotsSection';
import { createClient } from '@supabase/supabase-js';
import { 
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts/lib';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// Strategy Selection Modal Component
const StrategySelectionModal = ({ isOpen, onClose, strategies, clickedStrategy, onProceedToInvestment }) => {
  const [selectedStrategies, setSelectedStrategies] = useState([]);

  useEffect(() => {
    if (isOpen && clickedStrategy) {
      setSelectedStrategies([clickedStrategy.id]);
    } else if (!isOpen) {
      setSelectedStrategies([]);
    }
  }, [isOpen, clickedStrategy]);

  const handleStrategyToggle = (strategyId) => {
    setSelectedStrategies(prev => 
      prev.includes(strategyId) 
        ? prev.filter(id => id !== strategyId)
        : [...prev, strategyId]
    );
  };

  const handleProceed = () => {
    if (selectedStrategies.length > 0) {
      const selectedStrategyObjects = strategies.filter(s => selectedStrategies.includes(s.id));
      onProceedToInvestment(selectedStrategyObjects);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#151515] border border-white/10 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              Select Investment Strategies
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-400 mt-2">Choose one or more strategies to invest in:</p>
        </div>
        
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-4">
            {strategies.filter(s => s.status !== 'Closed').map((strategy) => (
              <div 
                key={strategy.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                  selectedStrategies.includes(strategy.id) 
                    ? 'border-[#676DFF] bg-[#676DFF]/5' 
                    : 'border-white/20 hover:border-white/30'
                }`}
                onClick={() => handleStrategyToggle(strategy.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={selectedStrategies.includes(strategy.id)}
                      onChange={() => handleStrategyToggle(strategy.id)}
                      className="w-4 h-4 text-[#676DFF] border-gray-300 rounded focus:ring-[#676DFF]"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-medium text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {strategy.name}
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">{strategy.briefSummary}</p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm font-medium text-gray-400">Category</div>
                        <div className="text-lg font-semibold text-emerald-400">{strategy.category}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-white/10">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400">
              {selectedStrategies.length} strategy(ies) selected
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
                style={{fontFamily: 'Lexend, sans-serif'}}
              >
                Cancel
              </button>
              <button
                onClick={handleProceed}
                disabled={selectedStrategies.length === 0}
                className="px-6 py-2 bg-[#676DFF] hover:bg-[#5A60E8] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                style={{fontFamily: 'Lexend, sans-serif'}}
              >
                Proceed to Investment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// KYC Status Modal Component 
const KYCStatusModal = ({ isOpen, onClose, kycStatus, onNavigateToProfile }) => {
  if (!isOpen) return null;

  const getStatusConfig = () => {
    switch (kycStatus) {
      case 'not_completed':
        return {
          icon: <AlertCircle className="h-12 w-12 text-amber-500" />,
          title: "Complete KYC to Start Investing",
          message: "Please complete your KYC verification to access our investment strategies.",
          buttonText: "Complete KYC",
          buttonClass: "bg-amber-500 hover:bg-amber-600",
          action: () => { onNavigateToProfile(); onClose(); }
        };
      case 'under_review':
        return {
          icon: <Clock className="h-12 w-12 text-blue-500" />,
          title: "KYC Under Review",
          message: "Your KYC is currently under review. Once approved, you can begin investing.",
          buttonText: "Check Status",
          buttonClass: "bg-blue-500 hover:bg-blue-600",
          action: () => { onNavigateToProfile(); onClose(); }
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6 lg:p-8 max-w-md w-full">
        <div className="flex justify-between items-start mb-6">
          <div className="text-center flex-1">
            <div className="flex justify-center mb-4">{config.icon}</div>
            <h3 className="text-xl font-semibold text-white mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>{config.title}</h3>
            <p className="text-gray-300 text-sm">{config.message}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 border border-white/20 text-gray-300 hover:bg-white/5 font-medium py-3 px-4 rounded-lg transition-colors"
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            Cancel
          </button>
          <button
            onClick={config.action}
            className={`flex-1 text-white font-medium py-3 px-4 rounded-lg transition-colors ${config.buttonClass}`}
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            {config.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Investment Modal Component
const InvestmentModal = ({ isOpen, onClose, strategy, userStatus, client }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    selectedStrategy: strategy?.id || '',
    amount: '',
    riskAppetite: 'medium',
    comments: ''
  });

  useEffect(() => {
    if (strategy) {
      const strategyId = Array.isArray(strategy) ? strategy[0]?.id : strategy.id;
      setFormData(prev => ({ ...prev, selectedStrategy: strategyId }));
    }
  }, [strategy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      const userData = {
        client_id: userStatus?.id || client?.id,
        client_name: userStatus?.name || client?.Name,
        client_email: userStatus?.email || client?.email,
        client_phone: userStatus?.phone || client?.phone
      };

      const strategiesArray = Array.isArray(strategy) ? strategy : [strategy];
      const selectedStrategyIds = strategiesArray.map(s => s.id || s.strategyId);
      const strategyNames = strategiesArray.map(s => s.name);

      const investmentData = {
        ...userData,
        selected_strategies: selectedStrategyIds,
        strategy_names: strategyNames,
        investment_amount: parseFloat(formData.amount),
        risk_appetite: formData.riskAppetite,
        comments: formData.comments,
        status: 'pending'
      };

      const { data, error } = await supabase
        .from('investment_requests')
        .insert([investmentData])
        .select();

      if (error) {
        throw error;
      }

      setFormData({
        selectedStrategy: '',
        amount: '',
        riskAppetite: 'medium',
        comments: ''
      });
      onClose();

      const strategyText = strategyNames.length > 1 
        ? `${strategyNames.length} strategies (${strategyNames.join(', ')})`
        : strategyNames[0];
      
      alert(`Investment request submitted successfully for ${strategyText}! We will contact you shortly.`);

    } catch (error) {
      console.error('Error submitting investment request:', error);
      alert('Failed to submit investment request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6 lg:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-xl font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Start Your Investment</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Selected Strateg{Array.isArray(strategy) && strategy.length > 1 ? 'ies' : 'y'}
            </label>
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              {Array.isArray(strategy) ? (
                <div className="space-y-2">
                  {strategy.map((s, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-white font-medium">{s.name}</span>
                      <span className="text-gray-400 text-sm">{s.category}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{strategy?.name}</span>
                  <span className="text-gray-400 text-sm">{strategy?.category}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Investment Amount (USD)</label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#676DFF] focus:outline-none"
              placeholder="Enter amount"
              min="100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Risk Appetite</label>
            <div className="grid grid-cols-3 gap-2">
              {['low', 'medium', 'high'].map((risk) => (
                <button
                  key={risk}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, riskAppetite: risk }))}
                  className={`p-2 rounded-lg border transition-colors text-sm font-medium ${
                    formData.riskAppetite === risk
                      ? 'bg-[#676DFF] border-[#676DFF] text-white'
                      : 'border-white/20 text-gray-300 hover:bg-white/5'
                  }`}
                >
                  {risk.charAt(0).toUpperCase() + risk.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Additional Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#676DFF] focus:outline-none h-20 resize-none"
              placeholder="Any specific requirements or questions..."
            />
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 border border-white/20 text-gray-300 hover:bg-white/5 font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#676DFF] hover:bg-[#5A60E8] disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Investment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const getPortfolioDisplayName = (portfolioType, portfolioId) => {
  const nameMapping = {
    // Vault mappings
    'BTC Vault': 'Nexus BTC',
    'ETH Vault': 'Nexus ETH', 
    'Stablecoin Vault': 'Nexus USD',
    'BTCV': 'Nexus BTC',
    'ETHV': 'Nexus ETH',
    'STABLEC': 'Nexus USD',
    
    // Strategy mappings
    'Altcoin Strategy': 'Nexus Nova',
    'Bluechip Strategy': 'Nexus Core', 
    'Smallcap Strategy': 'Nexus Alpha',
    'ALTC': 'Nexus Nova',
    'BLUEC': 'Nexus Core',
    'SMALLC': 'Nexus Alpha',
    
    // Additional fallbacks
    'Altcoin Portfolio': 'Nexus Nova',
    'Bluechip Portfolio': 'Nexus Core',
    'Smallcap Portfolio': 'Nexus Alpha'
  };
  
  return nameMapping[portfolioType] || nameMapping[portfolioId] || portfolioType || portfolioId;
};
  
const convertFrequencyToReadable = (frequency) => {
  // If it's already converted to readable format, return as is
  if (typeof frequency === 'string' && isNaN(frequency)) {
    return frequency;
  }
  
  // Convert seconds to readable format
  const seconds = parseInt(frequency);
  switch(seconds) {
    case 86400: return 'Daily';
    case 604800: return 'Weekly';
    case 1209600: return 'Bi-Weekly';
    case 2592000: return 'Monthly';
    default: return `Every ${Math.floor(seconds / 86400)} days`;
  }
};

const getDefaultHexCode = (assetId) => {
  const cryptoColors = {
    'BTC': '#F7931A',     // Bitcoin Orange
    'ETH': '#627EEA',     // Ethereum Blue
    'USDC': '#2775CA',    // USD Coin Blue
    'USDT': '#26A17B',    // Tether Green
    'BNB': '#F3BA2F',     // Binance Coin Yellow
    'ADA': '#0033AD',     // Cardano Blue
    'SOL': '#9945FF',     // Solana Purple
    'XRP': '#23292F',     // Ripple Dark
    'DOT': '#E6007A',     // Polkadot Pink
    'DOGE': '#C2A633',    // Dogecoin Gold
    'AVAX': '#E84142',    // Avalanche Red
    'SHIB': '#FFA409',    // Shiba Inu Orange
    'MATIC': '#8247E5',   // Polygon Purple
    'LTC': '#BFBBBB',     // Litecoin Silver
    'ATOM': '#2E3148',    // Cosmos Dark Blue
    'LINK': '#375BD2',    // Chainlink Blue
    'UNI': '#FF007A',     // Uniswap Pink
    'ALGO': '#000000',    // Algorand Black
    'VET': '#15BDFF',     // VeChain Light Blue
    'ICP': '#29ABE2',     // Internet Computer Blue
    'FIL': '#0090FF',     // Filecoin Blue
    'TRX': '#FF060A',     // Tron Red
    'ETC': '#328332',     // Ethereum Classic Green
    'XLM': '#000000',     // Stellar Black
    'THETA': '#2AB8E6',   // Theta Light Blue
    'FTM': '#1969FF',     // Fantom Blue
    'HBAR': '#000000',    // Hedera Black
    'MANA': '#FF2D55',    // Decentraland Red
    'SAND': '#00D4FF',    // Sandbox Cyan
    'AXS': '#0055D4',     // Axie Infinity Blue
    'FLOW': '#00EF8B',    // Flow Green
    'NEAR': '#000000',    // Near Black
    'APE': '#0957D0',     // ApeCoin Blue
    'CHZ': '#CD212A',     // Chiliz Red
    'GALA': '#000000',    // Gala Black
    'ENJ': '#624DBF'      // Enjin Purple
  };
  
  return cryptoColors[assetId?.toUpperCase()] || '#676DFF';
};

// Updated PortfolioDetail Component with Mobile Responsiveness
const PortfolioDetail = ({ portfolio, assets, onBack, btcLastUpdated, clientId, isAdminPreview = false }) => {
  const [assetData, setAssetData] = useState([]);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [navHistory, setNavHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showNavUpdatedTooltip, setShowNavUpdatedTooltip] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Mobile detection hook
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchPortfolioSummary();
    fetchAssetHoldings();
    fetchNavHistory();
  }, [portfolio]);


  

  const fetchPortfolioSummary = async () => {
    try {
      const url = isAdminPreview && clientId
        ? `${API_URL}/api/admin/view-as/${clientId}/portfolio/${portfolio.portfolioId}/summary`
        : `${API_URL}/api/client/portfolio/${portfolio.portfolioId}/summary`;
      const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Portfolio summary fetched:', data);
        setPortfolioSummary(data);
      } else {
        console.log('❌ Portfolio summary API failed, using fallback data');
        setPortfolioSummary({
          investment_amount: portfolio.investmentAmount || 0,
          fees_due: portfolio.feesDue || 0,
          current_value: portfolio.currentValue || 0,
          current_value_usd: portfolio.currentValue || 0
        });
      }
    } catch (error) {
      console.error('Error fetching portfolio summary:', error);
      setPortfolioSummary({
        investment_amount: portfolio.investmentAmount || 0,
        fees_due: portfolio.feesDue || 0,
        current_value: portfolio.currentValue || 0,
        current_value_usd: portfolio.currentValue || 0
      });
    }
  };

const fetchAssetHoldings = async () => {
  try {
    setIsLoading(true);
    const url = isAdminPreview && clientId
      ? `${API_URL}/api/admin/view-as/${clientId}/portfolio/${portfolio.portfolioId}/assets`
      : `${API_URL}/api/client/portfolio/${portfolio.portfolioId}/assets`;
    const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
    console.log('🔍 Fetching asset holdings for portfolio:', portfolio.portfolioId);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Asset holdings fetched from API:', data);
      
      // Process the API response data
      if (data && Array.isArray(data) && data.length > 0) {
        // Filter out zero-value holdings and process
        const validAssets = data.filter(asset => 
          (parseFloat(asset.value) || 0) > 0.01 || (parseFloat(asset.quantity) || 0) > 0
        );
        
        if (validAssets.length > 0) {
          // Get asset IDs to fetch hex codes from Supabase
          const assetIds = validAssets.map(asset => asset.assetId || asset.asset_id || asset.token);
          console.log('🔍 Looking for hex codes for API assets:', assetIds);
          
          // Fetch hex codes from Supabase for these specific assets
          const { data: hexData, error: hexError } = await supabase
            .from('idash_portfolio_inner_summary_asset_breakdown')
            .select('asset_id, hex_code')
            .eq('portfolio_id', portfolio.portfolioId)
            .in('asset_id', assetIds);
          
          console.log('🎨 Hex codes fetched for API assets:', hexData);
          
          // Create hex lookup map
          const hexLookup = {};
          if (hexData && !hexError) {
            hexData.forEach(item => {
              if (item.hex_code) {
                hexLookup[item.asset_id] = item.hex_code;
              }
            });
          }
          
          // Calculate total value for allocations
          const totalValue = validAssets.reduce((sum, asset) => 
            sum + Math.abs(parseFloat(asset.value) || 0), 0
          );
          
          // Process assets with proper formatting and hex codes
          const processedAssets = validAssets.map(asset => {
            const assetId = asset.assetId || asset.asset_id || asset.token;
            const hexFromDB = hexLookup[assetId];
            const hexFromAPI = asset.hexCode || asset.hex_code;
            const hexFromDefault = getDefaultHexCode(assetId);
            const finalHexCode = hexFromDB || hexFromAPI || hexFromDefault;
            
            console.log(`🎨 Asset ${assetId}: DB=${hexFromDB}, API=${hexFromAPI}, Default=${hexFromDefault}, Final=${finalHexCode}`);
            
            return {
              assetId: assetId,
              assetName: asset.assetName || asset.asset_name || asset.name,
              quantity: parseFloat(asset.quantity) || 0,
              price: parseFloat(asset.price) || 0,
              value: parseFloat(asset.value) || 0,
              allocation: totalValue > 0 ? (Math.abs(parseFloat(asset.value) || 0) / totalValue * 100) : 0,
              hexCode: finalHexCode
            };
          }).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
          
          console.log('✅ Final processed API assets with hex codes:', processedAssets);
          setAssetData(processedAssets);
        } else {
          console.log('📋 No valid assets found in API response');
          setAssetData([]);
        }
      } else {
        console.log('📋 Empty or invalid API response');
        setAssetData([]);
      }
    } else {
      console.log('❌ Asset holdings API failed, trying Supabase fallback');
      
      // Fallback to Supabase query if API fails
      const { data: portfolioHoldings, error: holdingsError } = await supabase
        .from('idash_portfolio_inner_summary_asset_breakdown')
        .select('*')
        .eq('portfolio_id', portfolio.portfolioId);
      
      if (!holdingsError && portfolioHoldings && portfolioHoldings.length > 0) {
        // Aggregate and process Supabase data as fallback
        const assetMap = new Map();
        
        portfolioHoldings.forEach(asset => {
          const assetId = asset.asset_id;
          const quantity = parseFloat(asset.quantity) || 0;
          const value = parseFloat(asset.value) || 0;
          const price = parseFloat(asset.price) || 0;
          
          if (assetMap.has(assetId)) {
            const existing = assetMap.get(assetId);
            existing.quantity += quantity;
            existing.value += value;
            if (existing.quantity > 0) {
              existing.price = existing.value / existing.quantity;
            }
          } else {
            assetMap.set(assetId, {
              asset_id: assetId,
              asset_name: asset.asset_name,
              quantity: quantity,
              value: value,
              price: price,
              hex_code: asset.hex_code
            });
          }
        });
        
        const aggregatedAssets = Array.from(assetMap.values())
          .filter(asset => Math.abs(asset.value) > 0.01 || asset.quantity > 0);
        
        const totalValue = aggregatedAssets.reduce((sum, asset) => 
          sum + Math.abs(asset.value), 0);
        
        const processedAssets = aggregatedAssets.map(asset => ({
          assetId: asset.asset_id,
          assetName: asset.asset_name,
          quantity: asset.quantity,
          price: asset.price,
          value: asset.value,
          allocation: totalValue > 0 ? (Math.abs(asset.value) / totalValue * 100) : 0,
          hexCode: asset.hex_code || getDefaultHexCode(asset.asset_id)
        })).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
        
        console.log('🔄 Fallback Supabase assets:', processedAssets);
        setAssetData(processedAssets);
      } else {
        setAssetData([]);
      }
    }
  } catch (error) {
    console.error('Error fetching asset holdings:', error);
    setAssetData([]);
  } finally {
    setIsLoading(false);
  }
};


const aggregateAssetHoldings = (rawAssets) => {
  const assetMap = new Map();
  
  rawAssets.forEach(asset => {
    const assetId = asset.asset_id;
    const quantity = parseFloat(asset.quantity) || 0;
    const value = parseFloat(asset.value) || 0;
    const price = parseFloat(asset.price) || 0;
    
    if (assetMap.has(assetId)) {
      // Aggregate existing asset
      const existing = assetMap.get(assetId);
      existing.quantity += quantity;
      existing.value += value;
      // Use volume-weighted average for price
      const totalQuantity = existing.quantity;
      if (totalQuantity > 0) {
        existing.price = existing.value / totalQuantity;
      }
    } else {
      // Add new asset
      assetMap.set(assetId, {
        asset_id: assetId,
        asset_name: asset.asset_name,
        quantity: quantity,
        value: value,
        price: price,
        hex_code: asset.hex_code
      });
    }
  });
  
  // Convert map back to array and sort by value (highest first)
  return Array.from(assetMap.values())
    .filter(asset => asset.quantity > 0 || asset.value > 0) // Remove zero holdings
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
};

  const fetchNavHistory = async () => {
    try {
      const url = isAdminPreview
        ? `${API_URL}/api/admin/view-as/portfolio/${portfolio.portfolioId}/nav-history`
        : `${API_URL}/api/client/portfolio/${portfolio.portfolioId}/nav-history`;
      const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
      console.log('🔍 Fetching NAV history for portfolio:', portfolio.portfolioId);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ NAV history fetched:', data);
        setNavHistory(data);
      } else {
        console.log('❌ NAV history API failed');
        setNavHistory(null);
      }
    } catch (error) {
      console.error('Error fetching NAV history:', error);
      setNavHistory(null);
    }
  };

  // Process real NAV data or generate fallback
  const processNavData = () => {
    if (navHistory && navHistory.nav_history && Object.keys(navHistory.nav_history).length > 0) {
      const navData = Object.entries(navHistory.nav_history)
        .map(([date, nav]) => ({
          date: date,
          nav: parseFloat(nav),
          displayDate: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      console.log('📈 Using real NAV data:', navData.length, 'points');
      return navData;
    } else {
      console.log('📈 Using mock NAV data - no real data available');
      return generateMockNavData();
    }
  };

  // Generate mock NAV chart data as fallback
  const generateMockNavData = () => {
    const baseNAV = 1.0;
    const data = [];
    const days = 365;
    const currentValueUSD = portfolioSummary?.current_value_usd || portfolio.currentValue || 0;
    const investmentAmountUSD = portfolio.investmentAmount || 0;
    
    const totalReturn = investmentAmountUSD > 0 ? (currentValueUSD - investmentAmountUSD) / investmentAmountUSD : 0;
    const dailyReturn = totalReturn / days;
    
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (days - i));
      
      const volatility = 0.015;
      const trend = dailyReturn * 0.7;
      const randomMove = (Math.random() - 0.5) * volatility;
      const navValue = i === 0 ? baseNAV : data[i-1].nav * (1 + trend + randomMove);
      
      data.push({
        date: date.toISOString().split('T')[0],
        nav: Math.max(navValue, 0.5),
        displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }
    
    return data;
  };

  const navData = processNavData();

  // Format currency without decimals for values, with decimals for amounts
  const formatCurrency = (amount, showCents = false) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0
    }).format(amount);
  };

  const formatDateDDMMYYYY = (dateValue) => {
    if (!dateValue) return '--';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format asset quantities based on asset type
  const formatQuantity = (quantity, assetId) => {
    if (quantity === null || quantity === undefined || isNaN(quantity)) return '0';
    
    const numQuantity = parseFloat(quantity);
    
    // Special formatting for BTC and ETH (5 decimals)
    if (assetId === 'BTC' || assetId === 'ETH') {
      return numQuantity.toFixed(5);
    }
    
    // Regular assets (2 decimals)
    return numQuantity.toFixed(2);
  };

  // Format percentage
  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined || isNaN(percent)) return '0.00%';
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  // Get trend color
  const getTrendColor = (value) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-[#B8B8B8]';
  };

  // Calculate metrics
  const investmentAmountUSD = portfolioSummary?.investment_amount_usd || portfolio.investmentAmount || 0;
  const investmentAmountAsset = portfolioSummary?.investment_amount_asset || 0;
  const feesDue = portfolioSummary?.fees_due || 0;
  const currentValueUSD = portfolioSummary?.current_value_usd || portfolio.currentValue || 0;
  const currentValueAsset = portfolioSummary?.current_value_asset || 0;
  
  const isVault = portfolio.portfolioId && portfolio.portfolioId.includes('V');
  const denominationAsset = portfolioSummary?.denomination_asset || portfolio.denominationAsset;
  const navHistoryDates = navHistory?.nav_history ? Object.keys(navHistory.nav_history) : [];
  const lastNavDate = navHistoryDates.length > 0 ? navHistoryDates.sort().slice(-1)[0] : null;
  const navLastUpdated = lastNavDate || btcLastUpdated;
  const navLastUpdatedDisplay = formatDateDDMMYYYY(navLastUpdated);
  const btcLastUpdatedDisplay = formatDateDDMMYYYY(btcLastUpdated);
  
  const profitLoss = currentValueUSD - investmentAmountUSD;
  const profitLossPercent = investmentAmountUSD > 0 ? (profitLoss / investmentAmountUSD) * 100 : 0;

  // Calculate total value for allocation percentages
  const totalValue = assetData.reduce((sum, asset) => sum + (parseFloat(asset.value) || 0), 0);

  // Mobile-friendly Metric Card Component
  const MetricCard = ({ title, value, icon: Icon, trend, isNegative = false, assetAmount, assetSymbol, subtitle }) => (
    <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6 relative overflow-hidden group hover:border-[#676DFF]/30 transition-all duration-300">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#676DFF] to-[#5A60E8]"></div>
      
      <div className="flex justify-between items-start mb-3 sm:mb-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
            <Icon className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
          </div>
          <h3 className="text-xs sm:text-sm text-[#B8B8B8] font-medium tracking-wide uppercase" style={{fontFamily: 'Lexend, sans-serif'}}>
            {title}
          </h3>
        </div>
      </div>
      
      <div className={`text-xl sm:text-2xl font-bold mb-2 tracking-tight ${isNegative ? 'text-red-400' : 'text-white'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
        {value}
      </div>
      
      {assetAmount && assetSymbol && (
        <div className="text-xs text-[#B8B8B8] mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>
          {formatQuantity(assetAmount, assetSymbol)} {assetSymbol}
        </div>
      )}
      
      {trend && (
        <div className="flex items-center space-x-2">
          <div className={`flex items-center space-x-1 text-xs sm:text-sm font-medium ${getTrendColor(parseFloat(trend.replace(/[^-0-9.]/g, '')))}`}>
            {trend.includes('+') ? (
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
            ) : trend.includes('-') ? (
              <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4" />
            ) : null}
            <span>{trend}</span>
          </div>
        </div>
      )}

      {subtitle && (
        <div className="text-xs text-[#808080] mt-2" style={{fontFamily: 'Lexend, sans-serif'}}>
          {subtitle}
        </div>
      )}
    </div>
  );

// Mobile-responsive NAV Chart Component
const NAVChart = () => (
  <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6">
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
      <h3 className="text-base sm:text-lg font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
        NAV Performance (All Time)
      </h3>
<div className="text-xs sm:text-sm text-[#B8B8B8]">
  Current NAV: <span className="text-white font-semibold">
    {(navHistory && navHistory.nav_history && Object.keys(navHistory.nav_history).length > 0) 
      ? Object.values(navHistory.nav_history)[Object.values(navHistory.nav_history).length - 1].toFixed(4)
      : '--'
    }
  </span>
</div>
    </div>
    
    <div className="h-64 sm:h-80">
      {(!navHistory || !navHistory.nav_history || Object.keys(navHistory.nav_history).length === 0) ? (
        // Skeleton loader for chart
        <div className="h-full bg-[#0A0A0A]/60 rounded-xl border border-[#676DFF]/10 p-4 relative">
          <div className="h-full flex flex-col justify-end space-y-2">
            {/* Skeleton bars representing chart data */}
            <div className="flex items-end justify-between h-full space-x-1">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="bg-[#676DFF]/20 rounded-sm animate-pulse"
                  style={{
                    height: `${Math.random() * 60 + 20}%`,
                    width: `${100 / 12 - 1}%`
                  }}
                ></div>
              ))}
            </div>
            {/* Skeleton axis labels */}
            <div className="flex justify-between mt-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-3 bg-[#676DFF]/10 rounded animate-pulse w-8"></div>
              ))}
            </div>
          </div>
          {/* Loading text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-6 h-6 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-[#808080] text-xs" style={{fontFamily: 'Lexend, sans-serif'}}>
                Loading NAV data...
              </p>
            </div>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={navData} margin={{ top: 5, right: isMobile ? 10 : 30, left: isMobile ? 10 : 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#676DFF/20" />
            <XAxis 
              dataKey="displayDate" 
              stroke="#B8B8B8"
              fontSize={isMobile ? 10 : 12}
              tick={{ fill: '#B8B8B8' }}
            />
            <YAxis 
              stroke="#B8B8B8"
              fontSize={isMobile ? 10 : 12}
              tick={{ fill: '#B8B8B8' }}
              domain={['dataMin * 0.98', 'dataMax * 1.02']}
              width={isMobile ? 45 : 60}
              tickFormatter={(value) => value.toFixed(4)}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#0A0A0A',
                border: '1px solid #676DFF',
                borderRadius: '8px',
                color: '#white',
                fontSize: isMobile ? '12px' : '14px'
              }}
              formatter={(value) => [value.toFixed(4), 'NAV']}
              labelFormatter={(label) => `Date: ${label}`}
            />
            <Line 
              type="monotone" 
              dataKey="nav" 
              stroke="#676DFF" 
              strokeWidth={2}
              dot={false}
              activeDot={{ r: isMobile ? 3 : 4, fill: '#676DFF' }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  </div>
);

  // Mobile-responsive Asset Allocation Chart
const AssetAllocationChart = () => {
  console.log('🎨 AssetAllocationChart - Raw assetData:', assetData);
  
  const pieData = assetData
    .map((asset, index) => {
      const hexCode = asset.hexCode;
      const fallbackColor = [
        '#676DFF', '#8B5CF6', '#10B981', '#F59E0B', 
        '#EF4444', '#06B6D4', '#84CC16', '#F97316'
      ][index % 8];
      
      console.log(`🎨 Asset ${asset.name}: hexCode=${hexCode}, fallback=${fallbackColor}`);
      
      return {
        name: asset.assetName || asset.name || asset.token,
        value: parseFloat(asset.value) || 0,
        color: hexCode || fallbackColor
      };
    })
    .sort((a, b) => b.value - a.value);

  console.log('📊 Final pieData with colors:', pieData);

  // REST OF YOUR EXISTING CODE REMAINS EXACTLY THE SAME
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = totalValue > 0 ? ((data.value / totalValue) * 100).toFixed(1) : '0.0';
      return (
        <div className="bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg p-3 shadow-xl">
          <p className="text-white font-semibold text-xs sm:text-sm mb-1" style={{fontFamily: 'Lexend, sans-serif'}}>
            {data.payload.name}
          </p>
          <p className="text-[#B8B8B8] text-xs mb-1">
            Value: {formatCurrency(data.value, true)}
          </p>
          <p className="text-[#B8B8B8] text-xs">
            {percentage}% of portfolio
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
        Asset Allocation
      </h3>
      
      {pieData.length === 0 ? (
        <div className="h-64 sm:h-80 bg-[#0A0A0A]/60 rounded-xl flex items-center justify-center border border-[#676DFF]/10">
          <div className="text-center p-4">
            <PieChart className="h-8 w-8 sm:h-12 sm:w-12 text-[#676DFF] opacity-40 mx-auto mb-4" />
            <p className="text-[#808080] text-xs sm:text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
              No asset allocation data available
            </p>
          </div>
        </div>
      ) : (
        <div className="h-64 sm:h-80">
          <div className="h-32 sm:h-48">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 25 : 40}
                  outerRadius={isMobile ? 55 : 80}
                  paddingAngle={3}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Mobile-optimized Legend */}
          <div className="mt-3 sm:mt-4 space-y-2 max-h-24 sm:max-h-32 overflow-y-auto">
            {pieData.map((entry, index) => {
              const percentage = totalValue > 0 ? ((entry.value / totalValue) * 100).toFixed(1) : '0.0';
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                    <div 
                      className="w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    <span className="text-xs sm:text-sm text-white font-medium truncate" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {entry.name}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <div className="text-xs sm:text-sm font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(entry.value)}
                    </div>
                    <div className="text-xs text-[#B8B8B8]">
                      {percentage}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

  // Mobile-responsive Strategy Holdings Table
const StrategyHoldingsTable = () => (
  <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl overflow-hidden">
    {/* Table Header */}
    <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#676DFF]/10">
      <h3 className="text-base sm:text-lg font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
        Strategy Holdings
      </h3>
      <span className="text-xs sm:text-sm text-[#B8B8B8]" style={{fontFamily: 'Lexend, sans-serif'}}>
        {assetData.length} Asset{assetData.length !== 1 ? 's' : ''}
      </span>
    </div>

    {isLoading ? (
      <div className="text-center py-12 sm:py-16">
        <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#808080] text-xs sm:text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
          Loading holdings...
        </p>
      </div>
    ) : assetData.length === 0 ? (
      <div className="text-center py-12 sm:py-16">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#676DFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-[#676DFF] opacity-60" />
        </div>
        <h4 className="text-white font-medium mb-2 text-sm sm:text-base" style={{fontFamily: 'Lexend, sans-serif'}}>
          No holdings found
        </h4>
        <p className="text-xs sm:text-sm text-[#808080] px-4" style={{fontFamily: 'Lexend, sans-serif'}}>
          Asset holdings will appear here once available
        </p>
      </div>
    ) : (
      <>
        {/* Mobile Card View */}
        {isMobile ? (
          <div className="divide-y divide-[#676DFF]/10">
            {assetData
              .map((asset, index) => {
                const allocation = totalValue > 0 
                  ? ((parseFloat(asset.value) / totalValue) * 100) 
                  : 0;
                return { ...asset, allocation };
              })
              .sort((a, b) => b.allocation - a.allocation)
              .map((asset, index) => {
                // GET HEX COLOR FOR ASSET ICON
                const assetId = asset.assetId || asset.token;
                const hexColor = asset.hexCode || getDefaultHexCode(assetId);
                
                return (
                  <div key={asset.assetId || index} className="p-4 hover:bg-[#676DFF]/5 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: hexColor }}
                        >
                          <span className="text-white text-xs font-bold">
                            {(assetId || asset.assetName || 'N/A').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-white text-sm truncate" style={{fontFamily: 'Lexend, sans-serif'}}>
                            {asset.assetName || asset.assetId}
                          </div>

                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {asset.allocation.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <div className="text-[#B8B8B8] mb-1">Quantity</div>
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatQuantity(asset.quantity, asset.assetId)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[#B8B8B8] mb-1">Price</div>
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(asset.price, true)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[#B8B8B8] mb-1">Value</div>
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(asset.value)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          /* Desktop Table View */
          <>
            <div className="bg-[#676DFF]/5 px-6 py-4 grid grid-cols-5 gap-4 text-xs font-semibold text-[#B8B8B8] uppercase tracking-wider" style={{fontFamily: 'Lexend, sans-serif'}}>
              <div>Asset</div>
              <div className="text-right">Quantity</div>
              <div className="text-right">Price</div>
              <div className="text-right">Value</div>
              <div className="text-right">Allocation</div>
            </div>

            <div className="divide-y divide-[#676DFF]/10">
              {assetData
                .map((asset, index) => {
                  const allocation = totalValue > 0 
                    ? ((parseFloat(asset.value) / totalValue) * 100) 
                    : 0;
                  return { ...asset, allocation };
                })
                .sort((a, b) => b.allocation - a.allocation)
                .map((asset, index) => {
                  // GET HEX COLOR FOR ASSET ICON
                  const assetId = asset.assetId || asset.token;
                  const hexColor = asset.hexCode || getDefaultHexCode(assetId);
                  
                  return (
                    <div key={asset.assetId || index} className="px-6 py-5 grid grid-cols-5 gap-4 items-center hover:bg-[#676DFF]/5 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: hexColor }}
                        >
                          <span className="text-white text-xs font-bold">
                            {(assetId || asset.assetName || 'N/A').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                            {asset.assetName || asset.assetId}
                          </div>

                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatQuantity(asset.quantity, asset.assetId)}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(asset.price, true)}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(asset.value)}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {asset.allocation.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </>
    )}
  </div>
);

  if (!portfolio) {
    return (
      <div className="text-center py-12 sm:py-16 px-4">
        <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Portfolio Not Found</h3>
        <p className="text-[#B8B8B8] mb-6 text-sm sm:text-base">The requested portfolio could not be loaded.</p>
        <Button onClick={onBack} variant="outline" className="bg-[#151515] border-[#676DFF]/30 text-[#676DFF]">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Overview
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
      
      {/* Header Section */}
      <div className="flex items-center justify-between">

      </div>

      {/* Portfolio Title */}
      <div className="flex items-center space-x-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>
          {getPortfolioDisplayName(portfolio.portfolioType || portfolio.portfolio_name, portfolio.portfolioId)}
        </h1>
        <div className="relative mb-2">
          <Info
            className="h-4 w-4 text-[#B8B8B8] hover:text-[#676DFF] cursor-help transition-colors"
            onMouseEnter={() => setShowNavUpdatedTooltip(true)}
            onMouseLeave={() => setShowNavUpdatedTooltip(false)}
          />
          {showNavUpdatedTooltip && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg text-xs text-white z-50 shadow-xl min-w-max">
              <div className="text-left">NAV last updated: {navLastUpdatedDisplay}</div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-[#676DFF]/30"></div>
            </div>
          )}
        </div>
      </div>

      {/* Portfolio Summary Cards - Mobile: 2x2 grid, Desktop: 4-card layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <MetricCard
          title="Investment Amount"
          value={formatCurrency(investmentAmountUSD, true)}
          icon={DollarSign}
          assetAmount={isVault ? investmentAmountAsset : null}
          assetSymbol={isVault ? denominationAsset : null}
        />
        <MetricCard
          title="Fees Due"
          value={formatCurrency(feesDue, true)}
          icon={AlertCircle}
        />
        <MetricCard
          title="Current Value"
          value={formatCurrency(currentValueUSD)}
          icon={TrendingUp}
          assetAmount={isVault ? currentValueAsset : null}
          assetSymbol={isVault ? denominationAsset : null}
          subtitle={btcLastUpdated ? `Updated: ${btcLastUpdatedDisplay}` : null}
        />
        <MetricCard
          title="Profit/Loss"
          value={formatCurrency(profitLoss)}
          icon={profitLoss >= 0 ? TrendingUp : TrendingDown}
          trend={formatPercentage(profitLossPercent)}
          isNegative={profitLoss < 0}
        />
      </div>

      {/* Charts Section - Mobile: Stacked, Desktop: Side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <NAVChart />
        </div>
        <div className="lg:col-span-1">
          <AssetAllocationChart />
        </div>
      </div>

      {/* Strategy Holdings Table */}
      <StrategyHoldingsTable />

      {/* Investment Lots Section */}
      <InvestmentLotsSection 
        portfolioId={portfolio.portfolioId} 
        portfolioName={portfolio.name}
        clientId={clientId}
        isAdminPreview={isAdminPreview}
      />
    </div>
  );
};

const PortfolioOverview = ({ 
  client, 
  investments, 
  sipData, 
  onRefresh, 
  onViewSIP,
  currentView = 'overview',
  currentPortfolio = null,
  onNavigate = () => {},
  userStatus,
  onNavigateToProfile,
  btcLastUpdated,
  isAdminPreview = false
}) => {

  const [isLoading, setIsLoading] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(currentPortfolio);
  const [showDetail, setShowDetail] = useState(currentView === 'detail');
  const [portfolioAssets, setPortfolioAssets] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  const [showKYCModal, setShowKYCModal] = useState(false);
const [showStrategySelectionModal, setShowStrategySelectionModal] = useState(false);
const [showInvestmentModal, setShowInvestmentModal] = useState(false);
const [clickedStrategy, setClickedStrategy] = useState(null);
const [selectedStrategiesForInvestment, setSelectedStrategiesForInvestment] = useState([]);
const [currentKycStatus, setCurrentKycStatus] = useState('not_completed');
const [availableStrategies, setAvailableStrategies] = useState([]);


  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const formatDateDDMMYYYY = (dateValue) => {
    if (!dateValue) return '--';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const btcLastUpdatedDisplay = formatDateDDMMYYYY(btcLastUpdated);

useEffect(() => {
  // Mock strategies data for the modal
  setAvailableStrategies([
    {
      id: 'nexus-btc',
      name: 'Nexus BTC',
      briefSummary: 'Build long-term Bitcoin exposure with enhanced yield.',
      category: 'Vault',
      status: 'Live'
    },
    {
      id: 'nexus-eth',
      name: 'Nexus ETH',
      briefSummary: 'Capture Ethereum\'s upside while compounding yield.',
      category: 'Vault',
      status: 'Live'
    },
    {
      id: 'nexus-usd',
      name: 'Nexus USD',
      briefSummary: 'Stable, consistent yield from USD-pegged assets.',
      category: 'Vault',
      status: 'Live'
    },
    {
      id: 'nexus-core',
      name: 'Nexus Core',
      briefSummary: 'The foundation of digital asset investing.',
      category: 'Portfolio',
      status: 'Open'
    },
    {
      id: 'nexus-nova',
      name: 'Nexus Nova',
      briefSummary: 'Positioned for the next era of blockchain adoption.',
      category: 'Portfolio',
      status: 'Open'
    },
    {
      id: 'nexus-alpha',
      name: 'Nexus Alpha',
      briefSummary: 'Harnessing early-stage innovation with active management.',
      category: 'Portfolio',
      status: 'Open'
    }
  ]);
}, []);

const handleContactAdvisor = () => {
  console.log('🔍 Contact advisor clicked');
  console.log('🔍 userStatus:', userStatus);
  console.log('🔍 userStatus.kycStatus:', userStatus?.kycStatus);
  console.log('🔍 userStatus.status:', userStatus?.status);
  
  const actualKycStatus = userStatus?.kycStatus || userStatus?.status || 'not_completed';
  console.log('🔍 actualKycStatus:', actualKycStatus);
  
  let mappedKycStatus;
  if (actualKycStatus === 'verified' || actualKycStatus === 'approved') {
    mappedKycStatus = 'approved';
  } else if (actualKycStatus === 'pending' || actualKycStatus === 'under_review' || actualKycStatus === 'submitted') {
    mappedKycStatus = 'under_review';
  } else {
    mappedKycStatus = 'not_completed';
  }
  
  console.log('🔍 mappedKycStatus:', mappedKycStatus);
  
  if (mappedKycStatus === 'approved') {
    console.log('🔍 Opening strategy selection modal');
    setShowStrategySelectionModal(true);
  } else {
    console.log('🔍 Opening KYC modal with status:', mappedKycStatus);
    setCurrentKycStatus(mappedKycStatus);
    setShowKYCModal(true);
  }
  
  // Force log the modal states after setting them
  setTimeout(() => {
    console.log('🔍 Modal states after update:');
    console.log('🔍 showKYCModal should be:', mappedKycStatus !== 'approved');
    console.log('🔍 showStrategySelectionModal should be:', mappedKycStatus === 'approved');
  }, 100);
};

const handleProceedToInvestment = (selectedStrategies) => {
  setSelectedStrategiesForInvestment(selectedStrategies);
  setShowStrategySelectionModal(false);
  setShowInvestmentModal(true);
};

  // Mobile detection hook
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setSelectedPortfolio(currentPortfolio);
    setShowDetail(currentView === 'detail');
  }, [currentView, currentPortfolio]);
  
  useEffect(() => {
    // Scroll to top when PortfolioDetail component mounts
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      const mainContent = document.querySelector('main .overflow-auto');
      if (mainContent) {
        mainContent.scrollTop = 0;
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 50);
    setTimeout(scrollToTop, 150);
  }, []);

  useEffect(() => {
    if (showDetail) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        const mainContent = document.querySelector('main .overflow-auto');
        if (mainContent) {
          mainContent.scrollTop = 0;
          mainContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [showDetail]);



  // Use ONLY client data
  const portfolioMetrics = {
    totalInvestment: client?.initialInvestment || 0,
    totalWithdrawn: client?.withdrawn || 0,
    netInvestment: (client?.initialInvestment || 0) - (client?.withdrawn || 0),
    currentValue: client?.currentValue || 0,
    totalReturn: client?.totalReturn || 0,
    profitLoss: client?.profitLoss || 0
  };

  console.log('🔍 Portfolio Metrics Details:', portfolioMetrics);

  // Calculate profit/loss and return percentage based on net investment
  const totalProfitLoss = portfolioMetrics.currentValue - portfolioMetrics.netInvestment;
  const totalReturnPercentage = portfolioMetrics.netInvestment > 0 
    ? ((totalProfitLoss / portfolioMetrics.netInvestment) * 100)
    : 0;

  // 🔧 FIXED: More lenient filtering with better debugging
const safeInvestments = investments || [];

const strategies = safeInvestments.filter(inv => {
  if (!inv || !inv.portfolioId) {
    return false;
  }
  
  const isVault = inv.portfolioId.includes('V') || inv.portfolioId === 'STABLEC';
  const hasValue = inv.currentValue > 0;
  
  return !isVault && hasValue;
});

const vaults = safeInvestments.filter(inv => {
  if (!inv || !inv.portfolioId) {
    return false;
  }
  
  const isVault = inv.portfolioId.includes('V') || inv.portfolioId === 'STABLEC';
  const hasValue = inv.currentValue > 0;
  
  return isVault && hasValue;
});

  // 🔧 ADD: Check if we should show empty state
  const hasAnyInvestments = strategies.length > 0 || vaults.length > 0 || (sipData?.plans?.length > 0);


  // Calculate strategy and vault totals for pie chart
  const strategyTotal = strategies.reduce((sum, inv) => sum + (inv.currentValue || 0), 0);
  const vaultTotal = vaults.reduce((sum, inv) => sum + (inv.currentValue || 0), 0);
  const sipTotal = sipData?.overview?.currentValue || 0;

  // Prepare pie chart data including SIP
const pieChartData = [];

if (strategyTotal > 0) {
  pieChartData.push({
    name: 'Investment Strategies',
    value: strategyTotal,
    count: strategies.length,
    color: '#676DFF' // Keep this as the strategies color
  });
}

if (vaultTotal > 0) {
  pieChartData.push({
    name: 'Investment Vaults',
    value: vaultTotal,
    count: vaults.length,
    color: '#8B5CF6' // Keep this as the vaults color
  });
}

if (sipTotal > 0) {
  pieChartData.push({
    name: 'SIP Investments',
    value: sipTotal,
    count: sipData?.plans?.length || 0,
    color: '#10B981' // Keep this as the SIP color
  });
}

  const handleRefresh = async () => {
    setIsLoading(true);
    if (onRefresh) {
      await onRefresh();
    }
    setIsLoading(false);
  };
  
const handleViewSIPDashboard = (plan) => {
  console.log('Viewing SIP plan:', plan);
  if (onViewSIP) {
    // Pass the plan data to parent component for SIP detail view
    onViewSIP(plan);
  }
  
  // If there's a navigation handler, use it to switch to SIP dashboard
  if (onNavigate) {
    onNavigate('sip-detail', plan);
  }
};

  const getBreadcrumbItems = () => {
    // Only show breadcrumb when in detail view (when there's actual navigation)
    if (!showDetail || !selectedPortfolio) {
      return []; // Return empty array for main page - no breadcrumb needed
    }

    const items = [
      {
        label: 'Dashboard',
        href: 'dashboard'
      },
      {
        label: 'Portfolio',
        href: 'portfolio'
      },
      {
        label: getPortfolioDisplayName(selectedPortfolio.portfolioType || selectedPortfolio.portfolio_name, selectedPortfolio.portfolioId),
        href: null // Current page, not clickable
      }
    ];

    return items;
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbNavigate = (href) => {
    switch (href) {
      case 'dashboard':
        // Navigate to main dashboard
        setShowDetail(false);
        setSelectedPortfolio(null);
        if (onNavigate) {
          onNavigate('dashboard');
        }
        break;
      case 'portfolio':
        // Navigate back to portfolio overview
        setShowDetail(false);
        setSelectedPortfolio(null);
        if (onNavigate) {
          onNavigate('portfolio');
        }
        break;
      default:
        break;
    }
  };

 // Fetch portfolio assets when viewing details
  const fetchPortfolioAssets = async (portfolioId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/portfolio/${portfolioId}/assets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const assets = await response.json();
        setPortfolioAssets(prev => ({
          ...prev,
          [portfolioId]: assets
        }));
        return assets;
      }
    } catch (error) {
      console.error('Error fetching portfolio assets:', error);
      return [];
    }
  };

  const handleViewPortfolio = async (portfolio) => {
    console.log('Viewing portfolio:', portfolio);
    setSelectedPortfolio(portfolio);
    
    // Fetch detailed asset breakdown for this portfolio
    if (!portfolioAssets[portfolio.portfolioId]) {
      await fetchPortfolioAssets(portfolio.portfolioId);
    }
    
    setShowDetail(true);
    
    // Notify parent component about navigation (for URL updates, etc.)
    if (onNavigate) {
      onNavigate('portfolio-detail', portfolio);
    }
    
    // Scroll to top when viewing portfolio detail
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      const mainContent = document.querySelector('main .overflow-auto');
      if (mainContent) {
        mainContent.scrollTop = 0;
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleBackToOverview = () => {
    setShowDetail(false);
    setSelectedPortfolio(null);
    
    // Notify parent component
    if (onNavigate) {
      onNavigate('portfolio');
    }
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      const mainContent = document.querySelector('main .overflow-auto');
      if (mainContent) {
        mainContent.scrollTop = 0;
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined || isNaN(percent)) return '0.00%';
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  // Get trend color
  const getTrendColor = (value) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-[#B8B8B8]';
  };

    if (!hasAnyInvestments && portfolioMetrics.currentValue === 0 && portfolioMetrics.totalInvestment === 0) {
    console.log('🎯 Showing contact advisor screen - no investments found');
    return (
  <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
    <div className="text-center py-12 sm:py-16">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#676DFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 text-[#676DFF] opacity-60" />
      </div>
      <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4">Contact Advisor to Begin Your Portfolio Journey</h3>
      <p className="text-sm sm:text-base text-[#B8B8B8] mb-8 max-w-md mx-auto">
        Your KYC is approved! Contact your advisor to start building your investment portfolio.
      </p>
<button
  onClick={() => {
    console.log('Button clicked!');
    alert('Button was clicked!');
    handleContactAdvisor();
  }}
  className="px-6 py-3 bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium rounded-lg transition-colors"
  style={{fontFamily: 'Lexend, sans-serif'}}
>
  Contact Advisor
</button>
    </div>
        {/* Add the modals here */}
    <KYCStatusModal 
      isOpen={showKYCModal}
      onClose={() => setShowKYCModal(false)}
      kycStatus={currentKycStatus}
      onNavigateToProfile={onNavigateToProfile}
    />

    <StrategySelectionModal
      isOpen={showStrategySelectionModal}
      onClose={() => setShowStrategySelectionModal(false)}
      strategies={availableStrategies}
      clickedStrategy={clickedStrategy}
      onProceedToInvestment={handleProceedToInvestment}
    />

    <InvestmentModal 
      isOpen={showInvestmentModal}
      onClose={() => setShowInvestmentModal(false)}
      strategy={selectedStrategiesForInvestment.length > 0 ? selectedStrategiesForInvestment : null}
      userStatus={userStatus}
      client={client}
    />
      </div>
    );
  }

 // Mobile-responsive Metric Card Component with Tooltip
  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isNegative = false, tooltip }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);

    useEffect(() => {
      if (!showTooltip) return;

      const handleClickOutside = (event) => {
        if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
          setShowTooltip(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [showTooltip]);

    return (
      <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6 relative overflow-hidden group hover:border-[#676DFF]/30 transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#676DFF] to-[#5A60E8]"></div>
        
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
              <Icon className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <h3 className="text-xs sm:text-sm text-[#B8B8B8] font-medium tracking-wide uppercase" style={{fontFamily: 'Lexend, sans-serif'}}>
                {title}
              </h3>
              {tooltip && (
                <div className="relative" ref={tooltipRef}>
                  <Info 
                    className="h-3 w-3 sm:h-4 sm:w-4 text-[#B8B8B8] hover:text-[#676DFF] cursor-help transition-colors"
                    onClick={() => setShowTooltip(prev => !prev)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setShowTooltip(prev => !prev);
                    }}
                  />
                  {showTooltip && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg text-xs text-white z-50 shadow-xl min-w-max">
                      <div className="space-y-1">
                        {tooltip.map((line, index) => (
                          <div key={index} className="text-left">{line}</div>
                        ))}
                      </div>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-[#676DFF]/30"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className={`text-xl sm:text-2xl font-bold mb-2 tracking-tight ${isNegative ? 'text-red-400' : 'text-white'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
          {value}
        </div>
        
        {trend && (
          <div className="flex items-center space-x-2">
            <div className={`flex items-center space-x-1 text-xs sm:text-sm font-medium ${getTrendColor(parseFloat(trend.replace(/[^-0-9.]/g, '')))}`}>
              {trend.includes('+') ? (
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : trend.includes('-') ? (
                <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : null}
              <span>{trend}</span>
            </div>
          </div>
        )}
        
        {subtitle && (
          <p className="text-xs text-[#808080] mt-2" style={{fontFamily: 'Lexend, sans-serif'}}>
            {subtitle}
          </p>
        )}
      </div>
    );
  };

  // Mobile-responsive Performance Chart Component
const PerformanceChart = () => {
  const [performanceData, setPerformanceData] = useState([]);
  const [isLoadingPerformance, setIsLoadingPerformance] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('ALL');
  const [performanceError, setPerformanceError] = useState(null);

  // Fetch performance history from Supabase
  const fetchPerformanceHistory = async () => {
    try {
      setIsLoadingPerformance(true);
      setPerformanceError(null);
      
      // Get client ID from client prop or localStorage
      const clientId = client?.clientId || client?.['Id no'] || localStorage.getItem('clientId');
      
      console.log('🔍 PerformanceChart: Fetching for client ID:', clientId);
      console.log('🔍 PerformanceChart: Client object:', client);
      
      if (!clientId) {
        console.log('❌ No client ID available for performance chart');
        setPerformanceError('Client ID not available');
        setIsLoadingPerformance(false);
        return;
      }

      console.log('🔍 Fetching performance history for client:', clientId);
      
      const { data, error } = await supabase
        .from('idash_investor_performance_history')
        .select('current_value_history')
        .eq('client_id', clientId)
        .single();

      console.log('📊 Supabase response:', { data, error });

      if (error) {
        console.error('❌ Error fetching performance history:', error);
        setPerformanceError('Chart will be updated shortly');
        setIsLoadingPerformance(false);
        return;
      }

      if (data && data.current_value_history) {
        // Process the historical data
        const historyEntries = Object.entries(data.current_value_history)
          .map(([date, value]) => ({
            date: date,
            value: parseFloat(value),
            displayDate: new Date(date).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: '2-digit'
            })
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        console.log('✅ Performance history loaded:', historyEntries.length, 'data points');
        console.log('📊 Sample data:', historyEntries.slice(0, 3));
        setPerformanceData(historyEntries);
      } else {
        console.log('📊 No performance history found for client');
        setPerformanceData([]);
      }
    } catch (error) {
      console.error('💥 Error in fetchPerformanceHistory:', error);
      setPerformanceError('Failed to load performance data: ' + error.message);
    } finally {
      setIsLoadingPerformance(false);
    }
  };

  // Fetch performance data on component mount
  useEffect(() => {
    fetchPerformanceHistory();
  }, [client?.clientId, client?.['Id no']]);

  // Filter data based on selected period
  const getFilteredData = () => {
    if (!performanceData.length) return [];
    
    const now = new Date();
    let startDate = new Date();
    
    switch (selectedPeriod) {
      case '1M':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case '3M':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case '6M':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case '1Y':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'ALL':
      default:
        return performanceData;
    }
    
    return performanceData.filter(item => new Date(item.date) >= startDate);
  };

  const filteredData = getFilteredData();
  
  // Calculate performance metrics
  const calculatePerformanceMetrics = () => {
    if (filteredData.length < 2) return { change: 0, changePercent: 0 };
    
    const firstValue = filteredData[0].value;
    const lastValue = filteredData[filteredData.length - 1].value;
    const change = lastValue - firstValue;
    const changePercent = firstValue > 0 ? (change / firstValue) * 100 : 0;
    
    return { change, changePercent };
  };

  const metrics = calculatePerformanceMetrics();

  // Custom Tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg p-3 shadow-xl">
          <p className="text-white font-semibold text-xs sm:text-sm mb-1" style={{fontFamily: 'Lexend, sans-serif'}}>
            {new Date(data.payload.date).toLocaleDateString('en-US', { 
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </p>
          <p className="text-[#676DFF] font-semibold">
            Portfolio Value: {formatCurrency(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-white mb-1" style={{fontFamily: 'Lexend, sans-serif'}}>
            Portfolio Value
          </h3>
          {filteredData.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-xs sm:text-sm text-[#B8B8B8]">
                Current: {formatCurrency(portfolioMetrics.currentValue)}
              </span>
              {metrics.changePercent !== 0 && (
                <div className={`flex items-center space-x-1 text-xs sm:text-sm font-medium ${
                  metrics.changePercent >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {metrics.changePercent >= 0 ? (
                    <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                  ) : (
                    <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                  <span>
                    {metrics.changePercent >= 0 ? '' : ''}{formatPercentage(metrics.changePercent)} 
                    ({selectedPeriod})
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex gap-1 bg-[#0A0A0A] rounded-lg p-1 self-start sm:self-auto">
          {['1M', '3M', '6M', '1Y', 'ALL'].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                selectedPeriod === period
                  ? 'bg-[#676DFF] text-white shadow-lg' 
                  : 'text-[#B8B8B8] hover:text-white hover:bg-[#676DFF]/20'
              }`}
              style={{fontFamily: 'Lexend, sans-serif'}}
            >
              {period}
            </button>
          ))}
        </div>
      </div>
      
      <div className="h-64 sm:h-96">
        {isLoadingPerformance ? (
          <div className="h-full bg-[#0A0A0A]/60 rounded-xl border border-[#676DFF]/10 p-4 relative">
            <div className="h-full flex flex-col justify-end space-y-2">
              <div className="flex items-end justify-between h-full space-x-1">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-[#676DFF]/20 rounded-sm animate-pulse"
                    style={{
                      height: `${Math.random() * 60 + 20}%`,
                      width: `${100 / 8 - 1}%`
                    }}
                  ></div>
                ))}
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-6 h-6 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-[#808080] text-xs" style={{fontFamily: 'Lexend, sans-serif'}}>
                  Loading performance data...
                </p>
              </div>
            </div>
          </div>
        ) : performanceError ? (
          <div className="h-full bg-[#0A0A0A]/60 rounded-xl flex items-center justify-center border border-[#676DFF]/10">
            <div className="text-center p-4">
              <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12 text-[#676DFF] opacity-40 mx-auto mb-4" />
              <h4 className="text-white font-medium mb-2 text-sm sm:text-base" style={{fontFamily: 'Lexend, sans-serif'}}>
                Chart will be updated shortly
              </h4>
              <p className="text-xs sm:text-sm text-[#B8B8B8] mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                We're preparing your performance data
              </p>
              <button
                onClick={fetchPerformanceHistory}
                className="px-4 py-2 bg-[#676DFF]/10 hover:bg-[#676DFF]/20 border border-[#676DFF]/30 text-[#676DFF] rounded-lg transition-all duration-200 text-xs font-medium"
                style={{fontFamily: 'Lexend, sans-serif'}}
              >
                <RefreshCw className="h-3 w-3 mr-1.5 inline" />
                Refresh
              </button>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="h-full bg-[#0A0A0A]/60 rounded-xl flex items-center justify-center border border-[#676DFF]/10">
            <div className="text-center p-4">
              <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-[#676DFF] opacity-40 mx-auto mb-4" />
              <h4 className="text-white font-medium mb-2 text-sm sm:text-base" style={{fontFamily: 'Lexend, sans-serif'}}>
                No Performance Data Available
              </h4>
              <p className="text-xs sm:text-sm text-[#808080]" style={{fontFamily: 'Lexend, sans-serif'}}>
                Performance history will appear here once your investments begin generating data
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filteredData} margin={{ top: 5, right: isMobile ? 10 : 30, left: isMobile ? 10 : 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#676DFF/20" />
              <XAxis 
                dataKey="displayDate" 
                stroke="#B8B8B8"
                fontSize={isMobile ? 10 : 12}
                tick={{ fill: '#B8B8B8' }}
                interval={isMobile ? Math.floor(filteredData.length / 4) : Math.floor(filteredData.length / 6)}
              />
              <YAxis 
                stroke="#B8B8B8"
                fontSize={isMobile ? 10 : 12}
                tick={{ fill: '#B8B8B8' }}
                domain={['dataMin * 0.95', 'dataMax * 1.05']}
                width={isMobile ? 60 : 80}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#676DFF" 
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: isMobile ? 4 : 5, fill: '#676DFF', strokeWidth: 2, stroke: '#FFFFFF' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};


  // Custom Pie Chart Tooltip
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const totalValue = portfolioMetrics.currentValue;
      const percentage = totalValue > 0 ? ((data.value / totalValue) * 100).toFixed(1) : '0.0';
      return (
        <div className="bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg p-3 shadow-xl">
          <p className="text-white font-semibold text-xs sm:text-sm mb-1" style={{fontFamily: 'Lexend, sans-serif'}}>
            {data.payload.name}
          </p>
          <p className="text-[#B8B8B8] text-xs mb-1">
            Value: {formatCurrency(data.value)}
          </p>
          <p className="text-[#B8B8B8] text-xs">
            {percentage}% of total portfolio
          </p>
        </div>
      );
    }
    return null;
  };

  // Mobile-responsive Investment Allocation Chart Component with SIP
const InvestmentAllocationChart = () => (
  <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6">
    <h3 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
      Investment Allocation
    </h3>
    
    {pieChartData.length === 0 ? (
      <div className="h-64 sm:h-96 bg-[#0A0A0A]/60 rounded-xl flex items-center justify-center border border-[#676DFF]/10">
        <div className="text-center p-4">
          <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-[#676DFF]/10 flex items-center justify-center mx-auto mb-4">
            <div className="w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 border-[#676DFF] border-t-transparent animate-spin"></div>
          </div>
          <p className="text-[#808080] text-xs sm:text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
            Allocation chart will appear once investments are made
          </p>
        </div>
      </div>
    ) : (
      <div className="h-64 sm:h-96">
        {/* Pie Chart Container */}
        <div className="h-40 sm:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                innerRadius={isMobile ? 30 : 50}
                outerRadius={isMobile ? 60 : 90}
                paddingAngle={3}
                dataKey="value"
                strokeWidth={0}
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Professional Legend */}
        <div className="mt-3 sm:mt-4 space-y-0.75 sm:space-y-3">
          {pieChartData.map((entry, index) => {
            const totalValue = portfolioMetrics.currentValue;
            const percentage = totalValue > 0 ? ((entry.value / totalValue) * 100).toFixed(1) : '0.0';
            return (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <div 
                    className="w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-xs sm:text-sm text-white font-medium truncate" style={{fontFamily: 'Lexend, sans-serif'}}>
                    {entry.name}
                  </span>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <div className="text-xs sm:text-sm font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                    {formatCurrency(entry.value)}
                  </div>
                  <div className="text-xs text-[#B8B8B8]">
                    {percentage}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

  // Mobile-responsive InvestmentTable component
const InvestmentTable = ({ title, investments, type, onViewPortfolio }) => {
  const totalPortfolioValue = portfolioMetrics.currentValue;

  return (
    <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl overflow-hidden">
      {/* Table Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#676DFF]/10">
        <h3 className="text-base sm:text-lg font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
          {title}
        </h3>
        <span className="text-xs sm:text-sm text-[#B8B8B8]" style={{fontFamily: 'Lexend, sans-serif'}}>
          {investments.length} Investment{investments.length !== 1 ? 's' : ''}
        </span>
      </div>

      {investments.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#676DFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Eye className="h-6 w-6 sm:h-8 sm:w-8 text-[#676DFF] opacity-60" />
          </div>
          <h4 className="text-white font-medium mb-2 text-sm sm:text-base" style={{fontFamily: 'Lexend, sans-serif'}}>
            No {type.toLowerCase()} investments yet
          </h4>
          <p className="text-xs sm:text-sm text-[#808080] px-4" style={{fontFamily: 'Lexend, sans-serif'}}>
            Contact your advisor to start investing in {type.toLowerCase()}
          </p>
        </div>
      ) : (
        <>
          {isMobile ? (
            <div className="divide-y divide-[#676DFF]/20 space-y-0 p-4">
              {investments.map((investment, index) => {
                const profitLoss = investment.profits || 0;
                const allocation = totalPortfolioValue > 0 
                  ? ((investment.currentValue / totalPortfolioValue) * 100) 
                  : 0;
                
                // ADDED: Get hex color for the portfolio
                const portfolioColor = getPortfolioHexColor(investment.portfolioId);
                
                return (
                  <div key={investment.portfolioId || index} className="p-4 hover:bg-[#676DFF]/5 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* UPDATED: Use hex color for portfolio icon */}
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: portfolioColor }}
                        >
                          <span className="text-white text-xs font-bold">
                            {(investment.portfolioType || investment.portfolioId || 'P').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                            {getPortfolioDisplayName(investment.portfolioType, investment.portfolioId)}
                          </div>
                          <div className="text-xs text-[#B8B8B8] mt-1">
                            {investment.isVault ? 'Vault' : 'Strategy'} • {allocation.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => onViewPortfolio(investment)}
                        className="px-3 py-1.5 bg-[#676DFF]/10 hover:bg-[#676DFF]/20 border border-[#676DFF]/30 hover:border-[#676DFF]/50 text-[#676DFF] hover:text-white rounded-lg transition-all duration-200 text-xs font-medium flex-shrink-0 ml-2"
                        style={{fontFamily: 'Lexend, sans-serif'}}
                      >
                        View
                      </button>
                    </div>
                    
                    {/* Rest of mobile card content remains the same */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-[#B8B8B8] mb-1">Investment</div>
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(investment.investmentAmount)}
                        </div>
                        {investment.isVault && 
                         investment.totalInvestedDenom && 
                         investment.denominationAsset && 
                         investment.denominationAsset !== 'USD' && (
                          <div className="text-xs text-[#B8B8B8] mt-1">
                            {parseFloat(investment.totalInvestedDenom).toFixed(6)} {investment.denominationAsset}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-[#B8B8B8] mb-1">Current Value</div>
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(investment.currentValue)}
                        </div>
                        {investment.isVault && 
                         investment.currentValueDenom && 
                         investment.denominationAsset && 
                         investment.denominationAsset !== 'USD' && (
                          <div className="text-xs text-[#B8B8B8] mt-1">
                            {parseFloat(investment.currentValueDenom).toFixed(6)} {investment.denominationAsset}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-[#676DFF]/10">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-[#B8B8B8] text-xs mb-1">P&L</div>
                          <div className={`font-semibold text-sm ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
                            {profitLoss >= 0 ? '' : ''}{formatCurrency(profitLoss)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[#B8B8B8] text-xs mb-1">Return</div>
                          <div className={`text-sm font-semibold ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
                            {investment.investmentAmount > 0 ? formatPercentage((profitLoss / investment.investmentAmount) * 100) : '0.00%'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop Table View */
            <>
              <div className="bg-[#676DFF]/5 px-6 py-4 grid grid-cols-6 gap-4 text-xs font-semibold text-[#B8B8B8] uppercase tracking-wider" style={{fontFamily: 'Lexend, sans-serif'}}>
                <div>Portfolio Name</div>
                <div className="text-right">Investment Amount</div>
                <div className="text-right">Current Value</div>
                <div className="text-right">P&L</div>
                <div className="text-right">Allocation</div>
                <div className="text-center">Action</div>
              </div>

              <div className="divide-y divide-[#676DFF]/10">
                {investments.map((investment, index) => {
                  const profitLoss = investment.profits || 0;
                  const allocation = totalPortfolioValue > 0 
                    ? ((investment.currentValue / totalPortfolioValue) * 100) 
                    : 0;
                  
                  // ADDED: Get hex color for the portfolio
                  const portfolioColor = getPortfolioHexColor(investment.portfolioId);
                  
                  return (
                    <div key={investment.portfolioId || index} className="px-6 py-5 grid grid-cols-6 gap-4 items-center hover:bg-[#676DFF]/5 transition-colors">
                      {/* Portfolio Name */}
                      <div className="flex items-center space-x-3">
                        {/* UPDATED: Use hex color for portfolio icon */}
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: portfolioColor }}
                        >
                          <span className="text-white text-xs font-bold">
                            {(investment.portfolioType || investment.portfolioId || 'P').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                            {getPortfolioDisplayName(investment.portfolioType, investment.portfolioId)}
                          </div>
                          <div className="text-xs text-[#B8B8B8] mt-1">
                            {investment.isVault ? 'Vault' : 'Strategy'}
                          </div>
                        </div>
                      </div>

                      {/* Rest of desktop table columns remain the same */}
                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(investment.investmentAmount)}
                        </div>
                        {investment.isVault && 
                         investment.totalInvestedDenom && 
                         investment.denominationAsset && 
                         investment.denominationAsset !== 'USD' && (
                          <div className="text-xs text-[#B8B8B8] mt-1">
                            {parseFloat(investment.totalInvestedDenom).toFixed(6)} {investment.denominationAsset}
                          </div>
                        )}
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {formatCurrency(investment.currentValue)}
                        </div>
                        {investment.isVault && 
                         investment.currentValueDenom && 
                         investment.denominationAsset && 
                         investment.denominationAsset !== 'USD' && (
                          <div className="text-xs text-[#B8B8B8] mt-1">
                            {parseFloat(investment.currentValueDenom).toFixed(6)} {investment.denominationAsset}
                          </div>
                        )}
                      </div>

                      <div className="text-right">
                        <div className={`font-semibold ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
                          {profitLoss >= 0 ? '+' : ''}{formatCurrency(profitLoss)}
                        </div>
                        <div className="text-xs text-[#B8B8B8] mt-1">
                          {investment.investmentAmount > 0 ? formatPercentage((profitLoss / investment.investmentAmount) * 100) : '0.00%'}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {allocation.toFixed(1)}%
                        </div>
                      </div>

                      <div className="text-center">
                        <button
                          onClick={() => onViewPortfolio(investment)}
                          className="px-3 py-1.5 bg-[#676DFF]/10 hover:bg-[#676DFF]/20 border border-[#676DFF]/30 hover:border-[#676DFF]/50 text-[#676DFF] hover:text-white rounded-lg transition-all duration-200 text-xs font-medium"
                          style={{fontFamily: 'Lexend, sans-serif'}}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const getPortfolioHexColor = (portfolioId) => {
  const portfolioColors = {
    // Vaults
    'BTCV': '#F7931A',    // Bitcoin Orange
    'ETHV': '#627EEA',    // Ethereum Blue
    'STABLEC': '#26A17B', // Stablecoin Green
    
    // Strategies
    'BLUEC': '#676DFF',   // Bluechip Purple
    'ALTC': '#8B5CF6',    // Altcoin Purple
    'SMALLC': '#EF4444',  // Smallcap Red
    
    // SIP
    'SIP': '#10B981'      // SIP Green
  };
  
  return portfolioColors[portfolioId] || '#676DFF';
};


  // Mobile-responsive SIP Plans Table Component
// 2. Fix SIP Plans Table in Portfolio Overview - Remove "Invested + Idle" text and add P&L percentage
const SIPPlansTable = ({ sipData }) => {
  if (!sipData?.plans || sipData.plans.length === 0) {
    return (
      <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#676DFF]/10">
          <h3 className="text-base sm:text-lg font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
            SIP Plans
          </h3>
          <span className="text-xs sm:text-sm text-[#B8B8B8]" style={{fontFamily: 'Lexend, sans-serif'}}>
            0 Plans
          </span>
        </div>
        <div className="text-center py-12 sm:py-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#10B981]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Repeat className="h-6 w-6 sm:h-8 sm:w-8 text-[#10B981] opacity-60" />
          </div>
          <h4 className="text-white font-medium mb-2 text-sm sm:text-base" style={{fontFamily: 'Lexend, sans-serif'}}>
            No SIP plans yet
          </h4>
          <p className="text-xs sm:text-sm text-[#808080] px-4" style={{fontFamily: 'Lexend, sans-serif'}}>
            Contact your advisor to start a Systematic Investment Plan
          </p>
        </div>
      </div>
    );
  }

  const totalPortfolioValue = portfolioMetrics.currentValue;

  return (
    <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#676DFF]/10">
        <h3 className="text-base sm:text-lg font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
          SIP Plans
        </h3>
        <div className="flex items-center space-x-2">
          <span className="text-xs sm:text-sm text-[#B8B8B8]" style={{fontFamily: 'Lexend, sans-serif'}}>
            {sipData.plans.length} Plan{sipData.plans.length !== 1 ? 's' : ''}
          </span>
          {sipData.overview?.idle > 0 && (
            <div className="relative group">
              <Info className="h-4 w-4 text-[#B8B8B8] hover:text-[#676DFF] cursor-help transition-colors" />
              <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg text-xs text-white z-50 shadow-xl min-w-max opacity-0 group-hover:opacity-100 transition-opacity">
                <div>Idle funds available for investment</div>
                <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-[#676DFF]/30"></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div className="divide-y divide-[#676DFF]/10">
          {sipData.plans.map((plan, index) => {
            const profitLoss = (plan.currentValue || 0) - (plan.totalInvested || 0);
            const profitLossPercent = plan.totalInvested > 0 ? (profitLoss / plan.totalInvested) * 100 : 0;
            
            return (
              <div key={`sip-${index}`} className="p-4 hover:bg-[#10B981]/5 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {plan.assetOut} SIP Plan
                    </div>
                    <div className="text-xs text-[#B8B8B8] mt-1 flex items-center space-x-2">
                      <Clock className="h-3 w-3" />
                      {/* APPLY CONVERSION HERE */}
                      <span>{convertFrequencyToReadable(plan.frequency)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewSIPDashboard(plan)}
                    className="px-3 py-1.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 hover:border-[#10B981]/50 text-[#10B981] hover:text-white rounded-lg transition-all duration-200 text-xs font-medium flex-shrink-0 ml-2"
                    style={{fontFamily: 'Lexend, sans-serif'}}
                  >
                    View
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                  <div>
                    <div className="text-[#B8B8B8] mb-1">Amount</div>
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(plan.amount)}
                    </div>
                    <div className="text-xs text-[#B8B8B8] mt-1">
                      {plan.assetIn} → {plan.assetOut}
                    </div>
                  </div>
                  <div>
                    {/* FIX 2: Remove "Invested + Idle" text, just show Total Invested */}
                    <div className="text-[#B8B8B8] mb-1">Total Invested</div>
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(plan.totalInvested)}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[#B8B8B8] mb-1">Current Value</div>
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(plan.currentValue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#B8B8B8] mb-1">Allocation</div>
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {totalPortfolioValue > 0 ? ((plan.currentValue / totalPortfolioValue) * 100).toFixed(1) : '0.0'}%
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-[#676DFF]/10">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-[#B8B8B8] text-xs mb-1">P&L</div>
                      <div className={`font-semibold text-sm ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
                        {profitLoss >= 0 ? '+' : ''}{formatCurrency(profitLoss)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#B8B8B8] text-xs mb-1">Return</div>
                      {/* FIX 3: Add percentage to P&L */}
                      <div className={`text-sm font-semibold ${profitLossPercent >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
                        {formatPercentage(profitLossPercent)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop Table View */
        <>
          <div className="bg-[#10B981]/5 px-6 py-4 grid grid-cols-7 gap-4 text-xs font-semibold text-[#B8B8B8] uppercase tracking-wider" style={{fontFamily: 'Lexend, sans-serif'}}>
            <div>Plan Details</div>
            <div className="text-right">Amount</div>
            <div className="text-right">Total Invested</div>
            <div className="text-right">Current Value</div>
            <div className="text-right">P&L</div>
            <div className="text-right">Allocation</div>
            <div className="text-center">Action</div>
          </div>

          <div className="divide-y divide-[#676DFF]/10">
            {sipData.plans.map((plan, index) => {
              const profitLoss = (plan.currentValue || 0) - (plan.totalInvested || 0);
              const profitLossPercent = plan.totalInvested > 0 ? (profitLoss / plan.totalInvested) * 100 : 0;
              
              return (
                <div key={`sip-desktop-${index}`} className="px-6 py-5 grid grid-cols-7 gap-4 items-center hover:bg-[#10B981]/5 transition-colors">
                  <div>
                    <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {plan.assetOut} SIP Plan
                    </div>
                    <div className="text-xs text-[#B8B8B8] mt-1 flex items-center space-x-2">
                      <Clock className="h-3 w-3" />
                      {/* APPLY CONVERSION HERE */}
                      <span>{convertFrequencyToReadable(plan.frequency)}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="font-semibold text-white text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(plan.amount)}
                    </div>
                    <div className="text-xs text-[#B8B8B8] mt-1">
                      {plan.assetIn} → {plan.assetOut}
                    </div>
                  </div>

                  {/* FIX 2: Remove "Invested + Idle" subtext */}
                  <div className="text-right">
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(plan.totalInvested)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {formatCurrency(plan.currentValue)}
                    </div>
                  </div>

                  {/* FIX 3: Add percentage to P&L */}
                  <div className="text-right">
                    <div className={`font-semibold ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
                      {profitLoss >= 0 ? '' : ''}{formatCurrency(profitLoss)}
                    </div>
                    <div className={`text-xs mt-1 ${profitLossPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatPercentage(profitLossPercent)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {totalPortfolioValue > 0 ? ((plan.currentValue / totalPortfolioValue) * 100).toFixed(1) : '0.0'}%
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={() => handleViewSIPDashboard(plan)}
                      className="px-3 py-1.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 hover:border-[#10B981]/50 text-[#10B981] hover:text-white rounded-lg transition-all duration-200 text-xs font-medium"
                      style={{fontFamily: 'Lexend, sans-serif'}}
                    >
                      View
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

  if (showDetail && selectedPortfolio) {
    return (
      <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
        {/* Breadcrumb for Detail View ONLY */}
        <Breadcrumb 
          items={getBreadcrumbItems()} 
          onNavigate={handleBreadcrumbNavigate}
        />
        
        <PortfolioDetail
          portfolio={selectedPortfolio}
          assets={portfolioAssets[selectedPortfolio.portfolioId] || []}
          onBack={handleBackToOverview}
          btcLastUpdated={btcLastUpdated}
          clientId={client?.['Id no']}
          isAdminPreview={isAdminPreview}
        />
      </div>
    );
  }

// Create tooltip content for Net Investment showing the breakdown
  const netInvestmentTooltip = [
    `Total Investment: ${formatCurrency(portfolioMetrics.totalInvestment)}`,
    `Total Withdrawn: ${formatCurrency(portfolioMetrics.totalWithdrawn)}`,
    `Net Investment: ${formatCurrency(portfolioMetrics.netInvestment)}`
  ];

  console.log('🎯 Rendering main portfolio overview with investments:', {
    strategies: strategies.length,
    vaults: vaults.length,
    sipPlans: sipData?.plans?.length || 0,
    hasAnyInvestments
  });

return (
  <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
    {/* Header Section */}
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[#B8B8B8] text-xs sm:text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
          Comprehensive view of your investment portfolio, SIP plans, and performance metrics
        </p>
      </div>
    </div>

    {/* Portfolio Summary Cards - Mobile: Single column, Desktop: 3 cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
      <MetricCard
        title="Net Investment"
        value={formatCurrency(portfolioMetrics.netInvestment)}
        icon={DollarSign}
        tooltip={netInvestmentTooltip}
        subtitle="Total invested minus withdrawals"
      />
      <MetricCard
        title="Current Value"
        value={formatCurrency(portfolioMetrics.currentValue)}
        icon={TrendingUp}
        trend={totalReturnPercentage >= 0 ? 
          `+${formatPercentage(totalReturnPercentage)}` : 
          `${formatPercentage(totalReturnPercentage)}`}
        subtitle={btcLastUpdated ? `Updated: ${btcLastUpdatedDisplay}` : null}
      />
      <MetricCard
        title="Total Return"
        value={formatCurrency(totalProfitLoss)}
        icon={totalProfitLoss >= 0 ? TrendingUp : TrendingDown}
        trend={`${formatPercentage(totalReturnPercentage)}`}
        isNegative={totalProfitLoss < 0}
      />
    </div>

    {/* Charts Section - Mobile: Stacked, Desktop: Side by side */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
<div className="lg:col-span-2">
  <PerformanceChart client={client} />
</div>
      <div className="lg:col-span-1">
        <InvestmentAllocationChart />
      </div>
    </div>

    {/* Investment Breakdown Tables - SMART ORDER */}
    <div className="space-y-6">
      {/* Show Vaults first if user has them */}
      {vaults.length > 0 && (
        <InvestmentTable
          title="Investment Vaults"
          investments={vaults}
          type="Vault"
          onViewPortfolio={handleViewPortfolio}
        />
      )}
      
      {/* Show Strategies if user has them */}
      {strategies.length > 0 && (
        <InvestmentTable
          title="Investment Strategies"
          investments={strategies}
          type="Strategy"
          onViewPortfolio={handleViewPortfolio}
        />
      )}
      
      {/* Show SIP if user has it */}
      {sipData?.plans?.length > 0 && sipData.plans.some(plan => plan.currentValue > 0) && (
        <SIPPlansTable sipData={sipData} />
      )}
      
      {/* Show empty sections for what user doesn't have */}
      {vaults.length === 0 && (
        <InvestmentTable
          title="Investment Vaults"
          investments={[]}
          type="Vault"
          onViewPortfolio={handleViewPortfolio}
        />
      
      )}
      
      {strategies.length === 0 && (
        <InvestmentTable
          title="Investment Strategies"
          investments={[]}
          type="Strategy"
          onViewPortfolio={handleViewPortfolio}
        />
      )}
      
      {(!sipData?.plans?.length || !sipData.plans.some(plan => plan.currentValue > 0)) && (
        <SIPPlansTable sipData={sipData} />
      )}
    </div>
    {/* Modals */}
      <KYCStatusModal 
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
        kycStatus={currentKycStatus}
        onNavigateToProfile={onNavigateToProfile}
      />

      <StrategySelectionModal
        isOpen={showStrategySelectionModal}
        onClose={() => setShowStrategySelectionModal(false)}
        strategies={availableStrategies}
        clickedStrategy={clickedStrategy}
        onProceedToInvestment={handleProceedToInvestment}
      />

      <InvestmentModal 
        isOpen={showInvestmentModal}
        onClose={() => setShowInvestmentModal(false)}
        strategy={selectedStrategiesForInvestment.length > 0 ? selectedStrategiesForInvestment : null}
        userStatus={userStatus}
        client={client}
      />
  </div>
);

};

// Export PortfolioDetail for dedicated routes
export { PortfolioDetail };

export default PortfolioOverview;